import { eventBus } from "#bb/lib/eventBus";
import type { AnswersFetchOptions } from "./useAnswersDriver";
import { useAnswersDriver } from "./useAnswersDriver";

export interface CachedAnswer {
  id: string;
  path: string;
  value: any;
  binding: any;
  data: any;
  type: string;
  timestamp: string;
}

function answerTime(ts?: string): number {
  const parsed = ts ? Date.parse(ts) : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

function matchesAnswer(
  answer: CachedAnswer,
  blockId: string,
  path?: string,
): boolean {
  return answer.id === blockId && (path === undefined || answer.path === path);
}

/**
 * Merge server answers into the local cache, keeping any locally-newer record (by timestamp).
 * The local cache is the live source of truth for the session; the server's aggregated
 * `cache/answers` can lag a just-made POST, so a re-fetch must never drop a newer local answer.
 */
function mergeAnswersByTimestamp(
  local: CachedAnswer[],
  incoming: CachedAnswer[],
): CachedAnswer[] {
  const byId = new Map<string, CachedAnswer>();
  for (const rec of incoming) {
    byId.set(`${rec.path}\0${rec.id}`, rec);
  }
  for (const rec of local) {
    const key = `${rec.path}\0${rec.id}`;
    const server = byId.get(key);
    if (!server || answerTime(rec.timestamp) > answerTime(server.timestamp)) {
      byId.set(key, rec);
    }
  }
  return Array.from(byId.values());
}

/**
 * useAnswers composable
 *
 * Usage:
 *   const { ... } = useAnswers();
 *     - Uses the current user's id for storage and caching
 */
export function useAnswers() {
  const { profile } = useProfile();
  const { getAnswersDriver } = useAnswersDriver();
  const driver = getAnswersDriver();

  const profileId = computed(
    () => profile.value?.id
  );
  const storeKey = `answersCache-${profileId.value}`;
  const cache = useState<CachedAnswer[]>(storeKey, () => []);

  /**
   * Reactive, synchronous read of a block's cached answer (no fetch). Used by building blocks to
   * bind their displayed value to the shared cache, so an answer made on any surface (the page or
   * the AI chat) is reflected live everywhere the block is mounted.
   */
  function getCachedAnswer(blockId: string, path?: string): CachedAnswer | null {
    return cache.value.find((answer) => matchesAnswer(answer, blockId, path)) || null;
  }

  async function fetchAnswers(options: AnswersFetchOptions = { force: false }) {
    try {
      const data = await driver.fetchAnswers(profileId.value, options);

      const incoming = (data || []).map((a: any) => ({
        id: a.id,
        path: a.path,
        value: a.value,
        binding: a.binding,
        data: a.data,
        type: a.answerType || a.type,
        timestamp: a.timestamp || new Date().toISOString(),
      }));

      // Merge rather than replace: the local cache is the live source of truth for the session
      // (answers made in the AI chat or on the page land here immediately), while the server's
      // aggregated `cache/answers` can lag a just-made POST. Keep any locally-newer record so a
      // force re-fetch (e.g. on resource load) never clobbers an answer the user just gave.
      cache.value = mergeAnswersByTimestamp(cache.value, incoming);

      return cache.value;
    } catch (err) {
      console.error("Error fetching all answers:", err);
      return cache.value;
    }
  }

  async function fetchAnswer(
    blockId: string,
    pathOrOptions?: string | AnswersFetchOptions,
    options: AnswersFetchOptions = { force: false }
  ) {
    const path = typeof pathOrOptions === "string" ? pathOrOptions : undefined;
    const fetchOptions = typeof pathOrOptions === "string"
      ? options
      : pathOrOptions || options;
    try {
      const data = await driver.fetchAnswer(
        profileId.value,
        blockId,
        path,
        fetchOptions
      );

      if (data?.id === blockId && (path === undefined || data.path === path)) {
        const record = {
          id: blockId,
          path: data.path,
          value: data.value,
          binding: data.binding,
          data: data.data,
          type: data.answerType || data.type,
          timestamp: data.timestamp || new Date().toISOString(),
        };
        // merge or push
        const idx = cache.value.findIndex((answer) =>
          matchesAnswer(answer, blockId, record.path)
        );
        if (idx !== -1) {
          cache.value[idx] = record;
        } else {
          cache.value.push(record);
        }
        return record;
      }
      return null;
    } catch (err) {
      console.error(`Error fetching answer for ${blockId}:`, err);
      return null;
    }
  }

  async function getAnswer(
    blockId: string,
    pathOrOptions?: string | AnswersFetchOptions,
    options: AnswersFetchOptions = { force: false }
  ) {
    const path = typeof pathOrOptions === "string" ? pathOrOptions : undefined;
    const fetchOptions = typeof pathOrOptions === "string"
      ? options
      : pathOrOptions || options;
    if (fetchOptions.force) {
      return await fetchAnswer(blockId, path, fetchOptions);
    }
    if (!cache.value.length) {
      await fetchAnswers(fetchOptions);
    }
    return getCachedAnswer(blockId, path);
  }

  async function setAnswer(
    blockId: string,
    value: any,
    options: { throwOnError?: boolean } = {}
  ) {
    const finalPath = value.path;
    const isUpdate = cache.value.some((answer) =>
      matchesAnswer(answer, blockId, finalPath)
    );

    try {
      const timestamp = new Date().toISOString();
      await driver.saveAnswer(
        profileId.value,
        blockId,
        {
          id: blockId,
          profileId: profileId.value,
          path: finalPath,
          value: value.value,
          binding: value.binding || {},
          data: value.data || {},
          type: value.type,
          timestamp,
        },
        isUpdate
      );

      const record = {
        id: blockId,
        path: finalPath,
        value: value.value,
        binding: value.binding || {},
        data: value.data || {},
        type: value.type,
        timestamp,
      };
      const idx = cache.value.findIndex((answer) =>
        matchesAnswer(answer, blockId, finalPath)
      );
      if (idx !== -1) {
        cache.value[idx] = record;
      } else {
        cache.value.push(record);
      }

      console.log(`Answer saved for ${blockId}`, value);
      eventBus.emit("component:onAnswerChanged", {
        id: blockId,
        ...value,
      });
      return record;
    } catch (err) {
      console.error(`Error setting answer for ${blockId}:`, err);
      if (options.throwOnError) {
        throw err;
      }
      return null;
    }
  }

  async function deleteAnswer(blockId: string, path?: string) {
    try {
      await driver.deleteAnswer(profileId.value, blockId, path);

      const idx = cache.value.findIndex((answer) =>
        matchesAnswer(answer, blockId, path)
      );
      if (idx !== -1) cache.value.splice(idx, 1);

      console.log(`Answer deleted for ${blockId}`);
      eventBus.emit("component:onAnswerChanged", {
        id: blockId,
        value: null,
      });
    } catch (err) {
      console.error(`Error deleting answer for ${blockId}:`, err);
    }
  }

  function getAnswersByPath(pattern: string) {
    const regex = new RegExp(
      "^" + pattern.replace(/\*/g, ".*").replace(/\//g, "\\/") + "$"
    );
    return cache.value
      .filter((r) => regex.test(r.path))
      .map((r) => ({
        ...r,
        binding: r.binding || {},
        data: r.data || {},
        value: r.value ?? null,
      }));
  }
  function getAnswersByPathAndType(pattern: string, type: string) {
    return getAnswersByPath(pattern).filter((r) => r.type === type);
  }
  function sumScoreByPathAndType(pattern: string, type: string) {
    return getAnswersByPathAndType(pattern, type).reduce(
      (sum, r) => sum + (r.data.score || 0),
      0
    );
  }
  function calculateByPathAndType(
    pattern: string,
    type: string,
    fn: (r: any) => number
  ) {
    return getAnswersByPathAndType(pattern, type).map(fn);
  }
  function sumMaxOptionValuesByPathAndType(pattern: string, type: string) {
    return getAnswersByPathAndType(pattern, type).reduce((sum, r) => {
      const max = Math.max(...r.binding.options.map((o: any) => o.value));
      return sum + max;
    }, 0);
  }

  return {
    getAnswer,
    getCachedAnswer,
    fetchAnswers,
    setAnswer,
    deleteAnswer,
    getAnswersByPath,
    getAnswersByPathAndType,
    sumScoreByPathAndType,
    calculateByPathAndType,
    sumMaxOptionValuesByPathAndType,
  };
}
